import instantsearch from 'instantsearch.js';
import { searchBox, hits, refinementList, toggleRefinement, pagination } from 'instantsearch.js/es/widgets';
import { connectPagination } from 'instantsearch.js/es/connectors';
const indexName = import.meta.env.VITE_ALGOLIA_INDEX_NAME;
// The server selects the public or member index from the authenticated session.
// No Algolia credential is shipped to the browser.
const searchClient = {
  async search(requests) {
    const response = await fetch('/actions/video-consortium-module/member-directory/search', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': window.csrfTokenValue },
      body: JSON.stringify({requests}),
    });
    if (!response.ok) throw new Error('Member search is unavailable. Please refresh and try again.');
    return response.json();
  },
};

function toKebabCase(value) {
  return value
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]/g, '');
}

function fromKebabCase(value) {
  return value
    .replace(/-/g, ' ') // Optional: convert dashes back to spaces
    .replace(/\b\w/g, l => l.toUpperCase()); // Optional: capitalize each word
}

function serializeArray(arr) {
  return Array.isArray(arr) ? arr.join(',') : undefined;
}

function deserializeArray(str) {
  return typeof str === 'string' ? str.split(',') : undefined;
}


const search = instantsearch({
  indexName: indexName,
  searchClient,
  future: {
    preserveSharedStateOnUnmount: true,
  },
  routing: {
    stateMapping: {
      stateToRoute(uiState) {
        const state = uiState[indexName] || {};
        return {
          query: state.query,
          skills: serializeArray(state.refinementList?.skills),
          experienceLevel: serializeArray(state.refinementList?.experienceLevel),
          spokenLanguages: serializeArray(state.refinementList?.spokenLanguages),
          populationGroupData: serializeArray(state.refinementList?.populationGroupData),
          lgbtqia: state.toggle?.lgbtqia,
          personOfColor: state.toggle?.personOfColor,
          availableForHire: state.toggle?.availableForHire,
          wantsFreelanceWork: state.toggle?.wantsFreelanceWork,
          wantsFullTimeWork: state.toggle?.wantsFullTimeWork,
          hiring: state.toggle?.hiring,
          openToVirtualCoffee: state.toggle?.openToVirtualCoffee,
          atANewsroom: state.toggle?.atANewsroom,
        };
      },
      routeToState(routeState) {
        return {
          [indexName]: {
            query: routeState.query,
            refinementList: {
              skills: deserializeArray(routeState.skills),
              experienceLevel: deserializeArray(routeState.experienceLevel),
              spokenLanguages: deserializeArray(routeState.spokenLanguages),
              populationGroupData: deserializeArray(routeState.populationGroupData),
            },
            toggle: {
              lgbtqia: routeState.lgbtqia === 'true',
              personOfColor: routeState.personOfColor === 'true',
              availableForHire: routeState.availableForHire === 'true',
              wantsFreelanceWork: routeState.wantsFreelanceWork === 'true',
              wantsFullTimeWork: routeState.wantsFullTimeWork === 'true',
              hiring: routeState.hiring === 'true',
              openToVirtualCoffee: routeState.openToVirtualCoffee === 'true',
              atANewsroom: routeState.atANewsroom === 'true',
            }
          }
        };
      }
    }
  }
});

window.memberSearch = search;
const widgets = [];

// Check for each container before adding the widget
if (document.querySelector('#searchbox')) {
  widgets.push(
    searchBox({
      container: '#searchbox',
      showSubmit: false,
      showReset: false,
      showLoadingIndicator: false,
      placeholder: 'Search by name, location, or key word...',
      cssClasses: {
        input: 'focus:ring-primary-500 focus:border-primary-500 block w-full pr-10 border-gray-300 rounded-md',
      },
    })
  );
}

if (document.querySelector('#hits')) {
  widgets.push(
    hits({
      container: '#hits',
      cssClasses: {
        list: 'grid grid-cols-1 md:grid-cols-12 gap-8', // Adds class to the root hits element (ul)
        item: 'member md:col-span-4 lg:col-span-3', // Adds class to each hit item element (li)
      },
      templates: {
        item(hit, { html }) {
          return html`
            <div class="test">
              <div class="image-wrapper group relative">
                <a href="/members/${hit.vanitySlug}">
                  ${hit.availableForHire ? html`
                    <div class="category absolute bottom-0 left-0 px-2 py-1 rounded-tr-lg rounded-bl-lg border-4 border-black/60 bg-black/60 text-secondary-500 uppercase vc-text-sm font-bold group-hover:border-secondary-500 group-hover:bg-secondary-500 group-hover:text-black">
                      Available For Hire
                    </div>
                  ` : ''}
                  ${hit.experienceLevel && hit.experienceLevel === 'Student' ? html`
                    <div class="category absolute top-0 right-0 px-2 py-1 rounded-tr-lg rounded-bl-lg border-4 border-black/60 bg-black/60 text-secondary-500 uppercase vc-text-sm font-bold group-hover:border-primary-500 group-hover:bg-primary-500 group-hover:text-white">
                      Student
                    </div>
                  ` : ''}
                  <img src="${hit.imageUrl}" alt="${hit.fullName} Photo" class="border-4 border-black rounded-lg group-hover:border-secondary-500" />
                </a>
              </div>
              <a href="/members/${hit.vanitySlug}" class="flex flex-row items-center pt-2">
                <h2 class="font-bold vc-text-xl mr-2">${hit.fullName}</h2>
                ${hit.isAdmin ? html`
                  <img data-wg-notranslate src="/img/icons/vc-organizer-logo.svg" class="w-[20px] h-auto" />` :
            hit.isGoldMember ? html`<img data-wg-notranslate src="/img/icons/gold-membership-logo.svg" alt="VC Gold member" class="w-[20px] h-auto" />` : ''}
              </a>
              ${hit.positionTitle || hit.company ? html`
                <div class="job-info vc-text-sm">
                  ${[hit.positionTitle, hit.company].filter(Boolean).join(', ')}
                </div>
              ` : ''}
              ${hit.city || hit.state || hit.country ? html`
                <div class="location-info vc-text-sm">
                  ${[hit.city, hit.state, hit.country].filter(Boolean).join(', ')}
                </div>
              ` : ''}
              ${(hit.skills && hit.skills.length) || hit.atANewsroom ? html`
                <div class="skills-info flex flex-row flex-wrap gap-2 mt-2">
                  ${hit.atANewsroom ? html`
                    <div class="bg-red-100 rounded-full px-2 py-1 text-red-900 vc-text-xs w-fit">
                      Newsroom
                    </div>
                  ` : ''}

                  ${hit.skills && hit.skills.length ? html`
                    ${hit.skills.map((skill, index) => html`
                      <div key="${index}" class="bg-gray-100 rounded-full px-2 py-1 text-gray-700 vc-text-xs w-fit">
                        ${skill}
                      </div>
                    `)}
                  ` : ''}
                </div>
              ` : ''}
              <a href="/members/${hit.vanitySlug}" class="mt-2 flex items-center">
                <span class="mr-2 text-sm">
                  More
                  <span class="sr-only"> about ${hit.fullName}</span>
                </span>
                <img data-wg-notranslate alt="See more about ${hit.fullName}" src="/img/icons/arrow-right.svg" />
              </a>
            </div>
          `;
        }
      }
    })
  );
}

const refinementContainers = [
  { container: '#skills-refinement-list', attribute: 'skills', sortBy: ['name:asc'] },
  { container: '#populationGroupData-refinement-list', attribute: 'populationGroupData' },
  { container: '#language-refinement-list', attribute: 'spokenLanguages' },
  { container: '#experience-refinement-list', attribute: 'experienceLevel', sortBy: ['count:desc'] },
];

refinementContainers.forEach(({ container, attribute, sortBy }) => {
  if (document.querySelector(container)) {
    widgets.push(
      refinementList({
        container,
        attribute,
        limit: 100,
        sortBy,
        templates: {
          item(item) {
            const { value, label, count, isRefined } = item;
            const formattedValue = toKebabCase(value); // Convert value to camelCase
            return `
              <div class="flex items-center">
                <input
                  value="${formattedValue}"
                  type="checkbox"
                  class="filter-checkbox | h-4 w-4 border-gray-300 rounded text-indigo-600 focus:ring-indigo-500"
                  ${isRefined ? 'checked' : ''}
                />
                <label class="ml-3 text-sm text-gray-700"
                  for="filter-category-${formattedValue}"
                >${label} (${count})</label>
              </div>
            `;
          },
        },
      })
    );
  }
});

const toggleContainers = [
  { container: '#lgbtqia-toggle-refinement', attribute: 'lgbtqia', label: 'LGBTQIA+' },
  { container: '#personOfColor-toggle-refinement', attribute: 'personOfColor', label: 'Person Of Color' },
  { container: '#availableForHire-toggle-refinement', attribute: 'availableForHire', label: 'Available For Hire' },
  { container: '#freelance-toggle-refinement', attribute: 'wantsFreelanceWork', label: 'Freelance' },
  { container: '#fullTime-toggle-refinement', attribute: 'wantsFullTimeWork', label: 'Full Time' },
  { container: '#hiring-toggle-refinement', attribute: 'hiring', label: 'Hiring' },
  { container: '#openToVirtualCoffee-toggle-refinement', attribute: 'openToVirtualCoffee', label: 'Open To Virtual Coffee' },
  { container: '#atANewsroom-toggle-refinement', attribute: 'atANewsroom', label: 'At a Newsroom' },
];

toggleContainers.forEach(({ container, attribute, label }) => {
  if (document.querySelector(container)) {
    widgets.push(
      toggleRefinement({
        container,
        attribute,
        cssClasses: {
          root: 'flex items-center',
          labelText: 'ml-3 text-sm text-gray-700',
          checkbox: 'filter-checkbox | h-4 w-4 border-gray-300 rounded text-indigo-600 focus:ring-indigo-500',
        },
        templates: {
          labelText({ count }, { html }) {
            return html`${label}`;
          },
        },
      })
    );
  }
});

if (document.querySelector('#pagination')) {
  const renderPagination = (renderOptions, isFirstRender) => {
    const {
      pages,
      currentRefinement,
      nbPages,
      isFirstPage,
      isLastPage,
      refine,
      createURL,
    } = renderOptions;

    const container = document.querySelector('#pagination');

    const getVisiblePages = () => {
      const visiblePages = [];

      if (nbPages <= 5) {
        return pages;
      }

      // Always show first page
      visiblePages.push(0);

      const currentPage = currentRefinement;
      const lastPage = nbPages - 1;

      // If we're close to the start
      if (currentPage <= 3) {
        visiblePages.push(1, 2, 3, 4);
        if (lastPage > 4) {
          visiblePages.push('...', lastPage);
        }
        return visiblePages;
      }

      // If we're close to the end
      if (currentPage >= lastPage - 3) {
        visiblePages.push(
          '...',
          lastPage - 4,
          lastPage - 3,
          lastPage - 2,
          lastPage - 1,
          lastPage
        );
        return visiblePages;
      }

      // We're in the middle
      visiblePages.push(
        '...',
        currentPage - 1,
        currentPage,
        currentPage + 1,
        '...',
        lastPage
      );

      return visiblePages;
    };

    const visiblePages = getVisiblePages();

    container.innerHTML = `
      <div class="pagination flex flex-wrap flex-row items-center gap-y-3">
        ${`<div class="max-sm:order-2 ml-0 mr-auto">
          <a href="${createURL(currentRefinement - 1)}" data-value="${currentRefinement - 1}" class="flex flex-row items-center ${isFirstPage ? 'opacity-0 pointer-events-none' : ''}">
            <div class="mr-2.5 rotate-180">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.5795 6.16478L14.2481 6.49618C14.0284 6.71584 14.0284 7.07199 14.2481 7.2917L18.1822 11.2031H0.5625C0.251859 11.2031 0 11.455 0 11.7656V12.2344C0 12.545 0.251859 12.7969 0.5625 12.7969H18.1822L14.2481 16.7083C14.0284 16.928 14.0284 17.2841 14.2481 17.5038L14.5795 17.8352C14.7992 18.0549 15.1553 18.0549 15.375 17.8352L20.8352 12.3977C21.0549 12.1781 21.0549 11.8219 20.8352 11.6022L15.375 6.16478C15.1553 5.94507 14.7992 5.94507 14.5795 6.16478Z"></path>
              </svg>
            </div>
            Previous
          </a>
        </div>
        <ul class="max-sm:order-1 flex flex-row items-center">
        `}
        ${visiblePages
          .map(
            page => {
              if (page === '...') {
                return '<li class="mx-4">...</li>';
              }
              return `
                <li class="mx-4">
                  <a
                    href="${createURL(page)}"
                    data-value="${page}"
                    class="${currentRefinement === page ? 'current w-10 h-10 bg-black rounded-full text-white flex items-center justify-center' : ''}"
                  >
                    ${page + 1}
                  </a>
                </li>
              `;
            }
          )
          .join('')}
          ${`
          </ul>
          <div class="max-sm:order-3 ml-auto mr-0">
            <a href="${createURL(currentRefinement + 1)}" data-value="${currentRefinement + 1}" class="flex flex-row items-center ${isLastPage ? 'opacity-0 pointer-events-none' : ''}">
              Next
              <div class="ml-2.5">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14.5795 6.16478L14.2481 6.49618C14.0284 6.71584 14.0284 7.07199 14.2481 7.2917L18.1822 11.2031H0.5625C0.251859 11.2031 0 11.455 0 11.7656V12.2344C0 12.545 0.251859 12.7969 0.5625 12.7969H18.1822L14.2481 16.7083C14.0284 16.928 14.0284 17.2841 14.2481 17.5038L14.5795 17.8352C14.7992 18.0549 15.1553 18.0549 15.375 17.8352L20.8352 12.3977C21.0549 12.1781 21.0549 11.8219 20.8352 11.6022L15.375 6.16478C15.1553 5.94507 14.7992 5.94507 14.5795 6.16478Z"></path>
                </svg>
              </div>
            </a>
          </div>`}
        </div>
    `;

    [...container.querySelectorAll('a')].forEach(element => {
      element.addEventListener('click', event => {
        event.preventDefault();

        // Scroll to the top of the results area
        document.querySelector('#searchbox')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });

        // Trigger Algolia to go to selected page
        refine(Number(event.currentTarget.dataset.value));
      });
    });
  };

  const customPagination = connectPagination(
    renderPagination
  );

  widgets.push(
    customPagination({
      container: document.querySelector('#pagination'),
    })
  );
}

// Add all widgets to the search instance
widgets.forEach(widget => search.addWidgets([widget]));

search.start();
